/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  forwardRef,
  useImperativeHandle,
  useId,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";

import { Autocomplete } from "components";
import { useSearchAutoComplete } from "hooks";
import { useAuth } from "hooks/useAuth";
import uniqBy from "lodash/uniqBy";
import { useQuery } from "react-query";
import { useAppSelector } from "stores";
import { getOptionsSelect } from "utils";

import { ModelName } from "./types";
import { getServices } from "./utils";

interface Props {
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  model: ModelName;
  defaultValue?: {
    label: string;
    value: string;
  };
  disabled?: boolean;
  keyValue?: string;
  keyLabel?: string;
  keySubLabel?: string;
  exclude?: string[];
  depField?: string | number;
  useDepField?: boolean;
  defaultFirstValue?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  filterLocal?: boolean;
}

const defaultPage = 1;
const defaultLimit = 50;

type ShortItem = {
  id: number | string;
  code: string;
  name: string;
  nativeName?: string;
};

const AutocompleteAsync = forwardRef((props: Props, componentRef) => {
  const {
    id,
    label,
    placeholder,
    required,
    model = "employee",
    disabled,
    onChange,
    defaultValue,
    keyValue = "id",
    keyLabel = "name",
    keySubLabel,
    exclude = [],
    defaultFirstValue,
    useDepField,
    depField,
    filterLocal = false,
  } = props;
  const queryKey = useId();
  const [hasMore, setHasMore] = useState(true);
  const [dataValue, setData] = useState<ShortItem[]>([]);
  const { userAppRole, organizations, apps } = useAuth();

  const [initDone, setInitDone] = useState(false);
  const org = useAppSelector((state) => state.auth.orgSelected);
  const searchAutocomplete = useSearchAutoComplete<string>(undefined);
  const enableQuery = useMemo(() => {
    if (userAppRole === "Employee") {
      if (["organization", "application"]?.includes(model)) {
        return false;
      }
    }
    if (useDepField) {
      return searchAutocomplete.isFocus && !!depField;
    }
    return defaultFirstValue ? true : searchAutocomplete.isFocus;
  }, [
    defaultFirstValue,
    depField,
    model,
    searchAutocomplete.isFocus,
    useDepField,
    userAppRole,
  ]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { isLoading } = useQuery({
    queryKey: [
      queryKey,
      searchAutocomplete.keyword,
      depField,
      searchAutocomplete.page,
    ],
    // eslint-disable-next-line no-nested-ternary
    enabled: enableQuery,
    queryFn: () =>
      getServices(
        model,
        searchAutocomplete.page,
        searchAutocomplete.keyword,
        depField
      ),
    onSuccess: (res) => {
      if (!initDone) {
        setInitDone(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const defaultValueItem: any = res?.data?.items?.find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (item: any) =>
            String(item?.[keyValue]) === String(defaultValue?.value)
        );
        if (defaultValueItem) {
          searchAutocomplete.onChange({
            label: defaultValueItem?.[keyLabel],
            value: defaultValueItem?.[keyValue],
          });
          onChange(defaultValueItem?.[keyValue]);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let shortItem: any = res.data.items?.[0];
          if (model === "organization") {
            const orgItem = res.data.items?.find((item) => item.id === org?.id);
            if (orgItem) {
              shortItem = orgItem;
            }
          }
          if (shortItem && defaultFirstValue) {
            searchAutocomplete.onChange({
              label: shortItem?.[keyLabel],
              value: shortItem?.[keyValue],
            });
            onChange(shortItem?.[keyValue]);
          }
        }
      }

      if (searchAutocomplete.page === defaultPage) {
        setData(res?.data?.items as ShortItem[]);
      } else {
        setData((prevData) =>
          uniqBy([...prevData, ...(res?.data?.items || [])], keyValue)
        );
      }
      if (res?.data?.items?.length === defaultLimit) {
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    },
  });

  const options = useMemo(() => {
    let optionsFilter = dataValue;
    if (userAppRole === "Employee") {
      if (["organization", "application"]?.includes(model)) {
        if (model === "organization") {
          optionsFilter = organizations as ShortItem[];
        }
        if (model === "application")
          optionsFilter = apps as unknown as ShortItem[];
      }
    }
    let optionFilters = [
      ...getOptionsSelect<string>({
        data: optionsFilter || [],
        keyLabel,
        keyValue,
        keySubLabel,
      }).filter((item) => !exclude?.includes(String(item.value))),
    ];
    if (filterLocal && searchAutocomplete.keyword) {
      optionFilters = optionFilters.filter((option) =>
        option.label
          .toLowerCase()
          .includes(searchAutocomplete.keyword.toLowerCase())
      );
    }
    return optionFilters;
  }, [
    dataValue,
    userAppRole,
    keyLabel,
    keyValue,
    keySubLabel,
    filterLocal,
    searchAutocomplete.keyword,
    model,
    apps,
    organizations,
    exclude,
  ]);

  useImperativeHandle(componentRef, () => ({
    reset: () => {
      searchAutocomplete.onChange(null);
    },
  }));

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      searchAutocomplete.setPage((prevPage) => prevPage + 1);
    }
  }, [hasMore, isLoading, searchAutocomplete]);

  useEffect(() => {
    if (userAppRole === "Employee") {
      let optionsFilter: any[] = [];
      if (["organization", "application"]?.includes(model)) {
        if (model === "organization") {
          optionsFilter = organizations as ShortItem[];
        }
        if (model === "application")
          optionsFilter = apps as unknown as ShortItem[];
        searchAutocomplete.onChange({
          label: optionsFilter?.[0]?.[keyLabel],
          value: optionsFilter?.[0]?.[keyValue],
        });
        onChange(optionsFilter?.[0]?.[keyValue]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Autocomplete
      id={id}
      value={searchAutocomplete.value}
      label={label}
      placeholder={placeholder}
      onChange={(newValue) => {
        searchAutocomplete.onChange(newValue);
        onChange(newValue?.value);
      }}
      options={options}
      onInputChange={searchAutocomplete.setKeyword}
      onFocus={searchAutocomplete.onFocus}
      required={required}
      disabled={disabled}
      loading={isLoading}
      onLoadMore={handleLoadMore}
      disabledLocalSearch
    />
  );
});

export default AutocompleteAsync;
